import { getI18nErrorTexts, getI18nTextIfExists } from "../i18n";
import { EntitleError, type TErrorTexts } from "./entitleError";
import { mapErrorParams, type TErrorId } from "./mapApiErrorParams";

export default class ApiError extends EntitleError {
  public readonly errorId?: TErrorId;
  public readonly params?: Record<string, unknown>;
  public readonly statusCode?: number;

  constructor({
    errorId,
    message,
    params,
    statusCode,
    suppressError = false,
    title,
  }: {
    errorId?: TErrorId;
    message?: string;
    params?: Record<string, unknown>;
    statusCode?: number;
    suppressError?: boolean;
    title?: string;
  }) {
    if (!message && !!errorId) {
      const i18nTexts = getI18nErrorTexts(errorId, mapErrorParams(errorId, params || {}));
      if (i18nTexts.message) {
        message = i18nTexts.message;
        title = i18nTexts.title || undefined;
      }
    }
    super(message, title, {}, suppressError);

    this.errorId = errorId;
    this.params = params;
    this.message = message || getI18nTextIfExists("error.unknown.message") || "";
    this.statusCode = statusCode;
  }

  static from(
    {
      errorId,
      message,
      params,
      title,
    }: {
      errorId?: TErrorId;
      message?: string;
      params?: { [key: string]: number | string };
      title?: string;
    },
    {
      errorId: overrideErrorId,
      statusCode,
      suppressError,
    }: { errorId?: TErrorId; statusCode?: number; suppressError?: boolean } = {},
  ) {
    if (!!title && !message) {
      message = title;
      title = undefined;
    }

    return new ApiError({
      errorId: overrideErrorId || errorId,
      message,
      params,
      statusCode,
      suppressError,
      title,
    });
  }

  override getActions() {
    if (this.errorId === "fetch.sessionExpired") {
      return {
        closeText: null,
        onRetry: () => window.location.reload(),
        retryText: getI18nTextIfExists("buttons.logIn"),
      };
    }
    return super.getActions();
  }

  override getTexts(): TErrorTexts {
    let i18nTexts: { message: null | string; title: null | string } = { message: null, title: null };
    if (this.errorId) {
      i18nTexts = getI18nErrorTexts(this.errorId, mapErrorParams(this.errorId, this.params || {}), !!this.message);
    }

    return {
      message: i18nTexts.message || this.message,
      title: i18nTexts.title || this.title,
    };
  }
}

export function getErrorTexts(error: ApiError | Error | null): TErrorTexts {
  if (!error)
    return {
      message: null,
      title: null,
    };

  if (error instanceof ApiError) return error.getTexts();

  return {
    message: error.message,
    title: null,
  };
}
